import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Autosuggest } from '@fs/zion-ui'
import { css, Global } from '@emotion/core'
import { i18n } from '@fs/zion-locale'

import { useCoverages } from '../../../../hooks'

const recordTypeLabel = () => i18n.t('coverage.recordTypeLabel', 'Record Type')
const standardRecordTypeErrorMessage = () =>
  i18n.t('editableCoverage.standardRecordTypeErrorMessage', 'You must select a standardized record type.')
const recordTypeRequiredMessage = () =>
  i18n.t('useCoverages.recordTypeRequiredMessage', 'Each place requires a record type.')

const globalCss = css`
  div[class^='groupedMenuCss'] {
    min-width: 320px;
  }
`

export default function EditableCoverageRecordType({
  errorMessage,
  forVolumeSet,
  placeId,
  recordType,
  disabled,
  allRecordTypes = [],
}) {
  const [recordTypeSuggestion, setRecordTypeSuggestion] = useState(recordType.name || '')
  const [selectRecordTypeMessage, setSelectRecordTypeMessage] = useState('')
  const [inputDirty, setInputDirty] = useState(false)
  const { setRecordType } = useCoverages({ forVolumeSet })

  const updateRecordType = useCallback(
    (value) => {
      if (!inputDirty) setInputDirty(true)
      setRecordTypeSuggestion(value)
    },
    [inputDirty]
  )

  const onBlur = useCallback(() => {
    let err = null
    if (inputDirty && recordTypeSuggestion) err = standardRecordTypeErrorMessage()
    else if (!recordTypeSuggestion) err = recordTypeRequiredMessage()
    setSelectRecordTypeMessage(err)
  }, [inputDirty, recordTypeSuggestion])

  const formattedRecordTypes =
    allRecordTypes?.length > 0
      ? allRecordTypes?.map((rt) => ({
          key: rt.id,
          primaryText: rt.name,
        }))
      : []

  const sortFunction = (inputValue, { primaryText: suggestionA }, { primaryText: suggestionB }) => {
    let result = 0
    if (inputValue && suggestionA && suggestionB) {
      const [i, a, b] = [inputValue, suggestionA, suggestionB].map((val) => val.toLowerCase())

      // We want to make sure that if either suggestions matches the input that is displayed first
      if (i === a) result = -1
      else if (i === b) result = 1
      else if (a < b) result = -1
      else if (b < a) result = 1
    }
    return result
  }

  const onSuggestionSelected = useCallback(
    (record) => {
      if (record) {
        setRecordType(placeId, recordType.id, {
          name: record.primaryText,
          id: Number(record.key),
        })
        setInputDirty(false)
        setSelectRecordTypeMessage(null)
      }
    },
    [placeId, recordType, setRecordType]
  )

  return (
    <>
      <Global styles={globalCss} />
      <Autosuggest
        suggestions={formattedRecordTypes}
        label={recordTypeLabel()}
        onSuggestionSelected={onSuggestionSelected}
        onInputTextChange={(input) => updateRecordType(input)}
        inputText={recordTypeSuggestion}
        initialSelectedItem={{ primaryText: recordType.name }}
        virtualize
        sortFunction={sortFunction}
        disabled={disabled}
        errorMessage={errorMessage || selectRecordTypeMessage}
        onBlur={onBlur}
      />
    </>
  )
}

EditableCoverageRecordType.propTypes = {
  errorMessage: PropTypes.string,
  forVolumeSet: PropTypes.bool,
  placeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  recordType: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    date: PropTypes.arrayOf(
      PropTypes.shape({
        fromTimestamp: PropTypes.number,
        toTimestamp: PropTypes.number,
        gedcomX: PropTypes.string,
      })
    ),
  }),
  allRecordTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
}
